import { useContext, useEffect } from 'react';
import { CartContext } from "../../store/context/CartContext";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Stack,
    Chip,
    IconButton,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Collapse,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    CircularProgress,
    Backdrop,
    Paper,
    alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import LinkIcon from '@mui/icons-material/Link';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddLinkIcon from '@mui/icons-material/AddLink';

import {
    DndContext,
    closestCenter,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useLinks } from '../../hooks/useLinks';

// ── Paleta ───────────────────────────────────────────────────────────────────
const PRIMARY = '#D0749B';
const FOLDER_COLOR = '#7C6AE8';

// ── Fila sortable ─────────────────────────────────────────────────────────────
const SortableLinkRow = ({
    link,
    expandedLinks,
    toggleExpand,
    handleToggleStatus,
    openEditDialog,
    openAddSubDialog,
    handleArchive,
}) => {
    const isFolder = link.hasSubLinks || link.subLinks?.length > 0;
    const accentColor = isFolder ? FOLDER_COLOR : PRIMARY;

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: link.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
        zIndex: isDragging ? 10 : 'auto',
    };

    return (
        <div ref={setNodeRef} style={style}>
            <Paper
                elevation={isDragging ? 6 : 0}
                sx={{
                    border: '1px solid',
                    borderColor: isDragging ? accentColor : 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    borderLeft: `4px solid ${accentColor}`,
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                    '&:hover': { boxShadow: 2 },
                }}
            >
                {/* Fila principal */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.5,
                        py: 1.25,
                        gap: 1,
                        bgcolor: isDragging ? alpha(accentColor, 0.04) : 'transparent',
                    }}
                >
                    {/* Handle de arrastre */}
                    <Tooltip title="Arrastrá para reordenar" placement="left">
                        <Box
                            {...attributes}
                            {...listeners}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.disabled',
                                flexShrink: 0,
                                cursor: 'grab',
                                borderRadius: 1,
                                p: 0.25,
                                transition: 'color 0.15s',
                                '&:hover': { color: 'text.secondary' },
                                '&:active': { cursor: 'grabbing' },
                            }}
                        >
                            <DragIndicatorIcon sx={{ fontSize: 18 }} />
                        </Box>
                    </Tooltip>

                    {/* Ícono tipo */}
                    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                        {isFolder
                            ? <FolderOpenIcon sx={{ color: FOLDER_COLOR, fontSize: 20 }} />
                            : <LinkIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                        }
                    </Box>

                    {/* Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                            {link.title}
                        </Typography>
                        {link.url ? (
                            <Typography
                                variant="caption"
                                noWrap
                                component="a"
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: 'text.secondary', display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                {link.url}
                            </Typography>
                        ) : (
                            <Typography variant="caption" sx={{ color: FOLDER_COLOR, fontWeight: 500 }}>
                                {link.subLinks?.length ?? 0} sub-link{(link.subLinks?.length ?? 0) !== 1 ? 's' : ''}
                            </Typography>
                        )}
                    </Box>

                    {/* Acciones */}
                    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                        <Chip
                            size="small"
                            label={link.isActive ? 'Activo' : 'Inactivo'}
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.68rem',
                                height: 22,
                                bgcolor: link.isActive ? alpha('#2e7d32', 0.1) : alpha('#000', 0.06),
                                color: link.isActive ? '#2e7d32' : 'text.secondary',
                                border: 'none',
                                display: { xs: 'none', sm: 'flex' },
                            }}
                        />

                        <Tooltip title={link.isActive ? 'Desactivar' : 'Activar'}>
                            <IconButton size="small" onClick={() => handleToggleStatus(link.id)}
                                sx={{ color: link.isActive ? '#ed6c02' : '#2e7d32' }}>
                                <PowerSettingsNewIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => openEditDialog(link, 'link')}
                                sx={{ color: 'text.secondary', '&:hover': { color: PRIMARY } }}>
                                <EditIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Agregar sub-link">
                            <IconButton size="small" onClick={() => openAddSubDialog(link.id, link.title)}
                                sx={{ color: FOLDER_COLOR, '&:hover': { bgcolor: alpha(FOLDER_COLOR, 0.08) } }}>
                                <AddIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Archivar">
                            <IconButton size="small" onClick={() => handleArchive(link.id)}
                                sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                                <ArchiveIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={expandedLinks[link.id] ? 'Cerrar' : 'Ver sub-links'}>
                            <IconButton size="small" onClick={() => toggleExpand(link.id)}
                                sx={{
                                    color: expandedLinks[link.id] ? accentColor : 'text.disabled',
                                    transition: 'transform 0.2s',
                                    transform: expandedLinks[link.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}>
                                <ExpandMoreIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>

                {/* Panel de sub-links */}
                <Collapse in={!!expandedLinks[link.id]}>
                    <Divider sx={{ borderColor: alpha(accentColor, 0.2) }} />
                    <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(accentColor, 0.03) }}>
                        {link.subLinks?.length > 0 ? (
                            <List dense disablePadding>
                                {link.subLinks.map((sub, idx) => (
                                    <ListItem
                                        key={sub.id}
                                        disablePadding
                                        sx={{
                                            py: 0.75,
                                            pl: 1,
                                            borderLeft: `2px solid ${alpha(accentColor, 0.3)}`,
                                            mb: idx < link.subLinks.length - 1 ? 0.5 : 0,
                                            borderRadius: '0 4px 4px 0',
                                            '&:hover': { bgcolor: alpha(accentColor, 0.04) },
                                        }}
                                    >
                                        <SubdirectoryArrowRightIcon sx={{ color: alpha(accentColor, 0.5), fontSize: 14, mr: 0.75, flexShrink: 0 }} />
                                        <ListItemText
                                            primary={sub.title}
                                            secondary={sub.url}
                                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                            secondaryTypographyProps={{
                                                variant: 'caption',
                                                noWrap: true,
                                                component: 'a',
                                                href: sub.url,
                                                target: '_blank',
                                                rel: 'noopener noreferrer',
                                                sx: { color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                                            }}
                                            sx={{ flex: 1, minWidth: 0 }}
                                        />
                                        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
                                            <Chip
                                                size="small"
                                                label={sub.isActive ? 'Activo' : 'Inactivo'}
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    bgcolor: sub.isActive ? alpha('#2e7d32', 0.1) : alpha('#000', 0.06),
                                                    color: sub.isActive ? '#2e7d32' : 'text.secondary',
                                                    display: { xs: 'none', sm: 'flex' },
                                                }}
                                            />
                                            <Tooltip title={sub.isActive ? 'Desactivar' : 'Activar'}>
                                                <IconButton size="small" onClick={() => handleToggleStatus(sub.id, true, link.id)}
                                                    sx={{ color: sub.isActive ? '#ed6c02' : '#2e7d32' }}>
                                                    <PowerSettingsNewIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Editar">
                                                <IconButton size="small" onClick={() => openEditDialog(sub, 'sublink', link.id)}
                                                    sx={{ color: 'text.secondary', '&:hover': { color: PRIMARY } }}>
                                                    <EditIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Archivar">
                                                <IconButton size="small" onClick={() => handleArchive(sub.id, true, link.id)}
                                                    sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                                                    <ArchiveIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                Sin sub-links todavía — usá el botón <strong>+</strong> para agregar uno.
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </Paper>
        </div>
    );
};

// ── Página principal ──────────────────────────────────────────────────────────
const LinksSireniles = () => {
    const { setTitleSeccion } = useContext(CartContext);
    const {
        links,
        loading,
        title,
        url,
        hasSubLinks,
        subLinksToCreate,
        editDialog,
        editTitle,
        editUrl,
        editIsActive,
        expandedLinks,
        addSubDialog,
        newSubTitle,
        newSubUrl,
        setTitle,
        setUrl,
        setHasSubLinks,
        setEditTitle,
        setEditUrl,
        setEditIsActive,
        setNewSubTitle,
        setNewSubUrl,
        getAllLinks,
        handleCreateLink,
        handleCreateSubLink,
        handleToggleStatus,
        handleArchive,
        openEditDialog,
        closeEditDialog,
        handleSaveEdit,
        toggleExpand,
        addSubLinkRow,
        removeSubLinkRow,
        updateSubLinkRow,
        openAddSubDialog,
        closeAddSubDialog,
        handleDragEnd,
    } = useLinks();

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
    );

    useEffect(() => {
        setTitleSeccion('Links Sireniles');
        getAllLinks();
        return () => setTitleSeccion('');
    }, []);

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '100%', boxSizing: 'border-box' }}>

            {/* ── Header ── */}
            <Box
                sx={{
                    mb: 3,
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(PRIMARY, 0.12)} 0%, ${alpha(FOLDER_COLOR, 0.08)} 100%)`,
                    border: `1px solid ${alpha(PRIMARY, 0.15)}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: PRIMARY,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <AddLinkIcon sx={{ color: 'white', fontSize: 22 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                            Links Sireniles
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Administrá los links de la app · Arrastrá para reordenar
                        </Typography>
                    </Box>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Chip
                        size="small"
                        label={`${links.length} link${links.length !== 1 ? 's' : ''}`}
                        sx={{ bgcolor: alpha(PRIMARY, 0.12), color: PRIMARY, fontWeight: 600 }}
                    />
                    <Chip
                        size="small"
                        label={`${links.filter(l => l.isActive).length} activos`}
                        sx={{ bgcolor: alpha('#2e7d32', 0.1), color: '#2e7d32', fontWeight: 600 }}
                    />
                </Stack>
            </Box>

            <Grid container spacing={{ xs: 2, md: 3 }}>

                {/* ── Columna izquierda: formulario ── */}
                <Grid item xs={12} md={4}>
                    <Box sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
                        <Card
                            elevation={0}
                            sx={{
                                border: `1px solid ${alpha(PRIMARY, 0.2)}`,
                                borderRadius: 3,
                                overflow: 'visible',
                            }}
                        >
                            {/* Card header decorado */}
                            <Box
                                sx={{
                                    px: 2.5,
                                    pt: 2.5,
                                    pb: 2,
                                    background: `linear-gradient(135deg, ${alpha(PRIMARY, 0.08)} 0%, transparent 100%)`,
                                    borderBottom: `1px solid ${alpha(PRIMARY, 0.1)}`,
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    Nuevo link
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Creá un link directo o una categoría con sub-links
                                </Typography>
                            </Box>

                            <CardContent sx={{ px: 2.5, pt: 2.5 }}>
                                <Stack spacing={2}>
                                    <TextField
                                        label="Título"
                                        placeholder="Ej: Documentales"
                                        size="small"
                                        fullWidth
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY }, '& label.Mui-focused': { color: PRIMARY } }}
                                    />

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: 2,
                                            bgcolor: hasSubLinks ? alpha(FOLDER_COLOR, 0.07) : alpha('#000', 0.03),
                                            border: `1px solid ${hasSubLinks ? alpha(FOLDER_COLOR, 0.2) : 'transparent'}`,
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                Tiene sub-links
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Activa para crear una categoría
                                            </Typography>
                                        </Box>
                                        <Switch
                                            checked={hasSubLinks}
                                            onChange={(e) => {
                                                setHasSubLinks(e.target.checked);
                                                if (e.target.checked) setUrl('');
                                            }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: FOLDER_COLOR },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: FOLDER_COLOR },
                                            }}
                                        />
                                    </Box>

                                    {!hasSubLinks && (
                                        <TextField
                                            label="URL"
                                            placeholder="https://..."
                                            size="small"
                                            fullWidth
                                            value={url}
                                            onChange={(e) => setUrl(e.target.value)}
                                            sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY }, '& label.Mui-focused': { color: PRIMARY } }}
                                        />
                                    )}

                                    {hasSubLinks && (
                                        <Box>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: FOLDER_COLOR, mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                Sub-links a crear
                                            </Typography>
                                            <Stack spacing={1}>
                                                {subLinksToCreate.map((row, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 0.75,
                                                            alignItems: 'center',
                                                            p: 1,
                                                            borderRadius: 1.5,
                                                            bgcolor: alpha(FOLDER_COLOR, 0.04),
                                                            border: `1px solid ${alpha(FOLDER_COLOR, 0.1)}`,
                                                        }}
                                                    >
                                                        <Typography variant="caption" sx={{ color: FOLDER_COLOR, fontWeight: 700, minWidth: 16, textAlign: 'center' }}>
                                                            {index + 1}
                                                        </Typography>
                                                        <TextField
                                                            size="small"
                                                            placeholder="Título"
                                                            value={row.title}
                                                            onChange={(e) => updateSubLinkRow(index, 'title', e.target.value)}
                                                            sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
                                                        />
                                                        <TextField
                                                            size="small"
                                                            placeholder="URL"
                                                            value={row.url}
                                                            onChange={(e) => updateSubLinkRow(index, 'url', e.target.value)}
                                                            sx={{ flex: 1.5, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
                                                        />
                                                        {subLinksToCreate.length > 1 && (
                                                            <IconButton size="small" onClick={() => removeSubLinkRow(index)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                                                                <CloseIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        )}
                                                    </Box>
                                                ))}
                                            </Stack>
                                            <Button
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={addSubLinkRow}
                                                sx={{ mt: 1, color: FOLDER_COLOR, '&:hover': { bgcolor: alpha(FOLDER_COLOR, 0.06) } }}
                                            >
                                                Agregar fila
                                            </Button>
                                        </Box>
                                    )}
                                </Stack>
                            </CardContent>

                            <CardActions sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleCreateLink}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                    sx={{
                                        bgcolor: PRIMARY,
                                        '&:hover': { bgcolor: alpha(PRIMARY, 0.85) },
                                        borderRadius: 2,
                                        py: 1,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '0.95rem',
                                    }}
                                >
                                    {loading ? 'Creando...' : 'Crear link'}
                                </Button>
                            </CardActions>
                        </Card>
                    </Box>
                </Grid>

                {/* ── Columna derecha: lista ── */}
                <Grid item xs={12} md={8}>
                    <Card
                        elevation={0}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 3,
                        }}
                    >
                        {/* Header lista */}
                        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Links creados
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {links.length === 0 ? 'Todavía no hay links.' : `${links.length} link${links.length !== 1 ? 's' : ''} · arrastrá ⠿ para reordenar`}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider />

                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                            {links.length === 0 ? (
                                <Box
                                    sx={{
                                        py: 6,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        color: 'text.disabled',
                                    }}
                                >
                                    <AddLinkIcon sx={{ fontSize: 48, color: alpha(PRIMARY, 0.3) }} />
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        Todavía no hay links cargados
                                    </Typography>
                                    <Typography variant="caption">
                                        Creá el primero desde el formulario de la izquierda
                                    </Typography>
                                </Box>
                            ) : (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext items={links.map(l => l.id)} strategy={verticalListSortingStrategy}>
                                        <Stack spacing={1}>
                                            {links.map((link) => (
                                                <SortableLinkRow
                                                    key={link.id}
                                                    link={link}
                                                    expandedLinks={expandedLinks}
                                                    toggleExpand={toggleExpand}
                                                    handleToggleStatus={handleToggleStatus}
                                                    openEditDialog={openEditDialog}
                                                    openAddSubDialog={openAddSubDialog}
                                                    handleArchive={handleArchive}
                                                />
                                            ))}
                                        </Stack>
                                    </SortableContext>
                                </DndContext>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Backdrop */}
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
                <CircularProgress sx={{ color: PRIMARY }} />
            </Backdrop>

            {/* ── Dialog: agregar sub-link ── */}
            <Dialog open={addSubDialog.open} onClose={closeAddSubDialog} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${alpha(FOLDER_COLOR, 0.2)}` } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Agregar sub-link
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        en "{addSubDialog.parentTitle}"
                    </Typography>
                    <IconButton onClick={closeAddSubDialog} size="small"
                        sx={{ position: 'absolute', right: 12, top: 12, color: 'text.disabled' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2}>
                        <TextField
                            label="Título"
                            size="small"
                            fullWidth
                            autoFocus
                            value={newSubTitle}
                            onChange={(e) => setNewSubTitle(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: FOLDER_COLOR }, '& label.Mui-focused': { color: FOLDER_COLOR } }}
                        />
                        <TextField
                            label="URL"
                            placeholder="https://..."
                            size="small"
                            fullWidth
                            value={newSubUrl}
                            onChange={(e) => setNewSubUrl(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: FOLDER_COLOR }, '& label.Mui-focused': { color: FOLDER_COLOR } }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeAddSubDialog} variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleCreateSubLink}
                        disabled={loading}
                        startIcon={<AddIcon />}
                        sx={{ bgcolor: FOLDER_COLOR, '&:hover': { bgcolor: alpha(FOLDER_COLOR, 0.85) }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        Agregar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Dialog: editar ── */}
            <Dialog open={editDialog.open} onClose={closeEditDialog} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${alpha(PRIMARY, 0.2)}` } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {editDialog.type === 'link' ? 'Editar link' : 'Editar sub-link'}
                    </Typography>
                    <IconButton onClick={closeEditDialog} size="small"
                        sx={{ position: 'absolute', right: 12, top: 12, color: 'text.disabled' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2}>
                        <TextField
                            label="Título"
                            size="small"
                            fullWidth
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY }, '& label.Mui-focused': { color: PRIMARY } }}
                        />
                        {(editDialog.type === 'sublink' || !editDialog.item?.hasSubLinks) && (
                            <TextField
                                label="URL"
                                size="small"
                                fullWidth
                                value={editUrl}
                                onChange={(e) => setEditUrl(e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY }, '& label.Mui-focused': { color: PRIMARY } }}
                            />
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 2,
                                bgcolor: editIsActive ? alpha('#2e7d32', 0.06) : alpha('#000', 0.03),
                                border: `1px solid ${editIsActive ? alpha('#2e7d32', 0.2) : 'transparent'}`,
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {editIsActive ? 'Activo en la app' : 'Inactivo (oculto)'}
                            </Typography>
                            <Switch
                                size="small"
                                checked={editIsActive}
                                onChange={(e) => setEditIsActive(e.target.checked)}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#2e7d32' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2e7d32' },
                                }}
                            />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeEditDialog} variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveEdit}
                        disabled={loading}
                        sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: alpha(PRIMARY, 0.85) }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        Guardar cambios
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LinksSireniles;
